import React, { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Crown, Search, Trophy } from "lucide-react";
import { UserProfileDialog } from "@/components/UserProfileDialog";

const CATEGORIES = ["All", "Gaming", "Entertainment", "Music", "IRL", "Finance", "Podcasts", "Memes", "Lifestyle", "Other"];
const PLATFORMS = ["All", "TikTok", "Instagram", "YouTube"];

function compact(n) {
  const value = Number(n || 0);
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 1 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 10_000 ? 0 : 1)}K`;
  return value.toLocaleString();
}

function avatar(entry) {
  return entry.avatar_url || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(entry.username || "User")}&backgroundColor=c8ff00&textColor=000000`;
}

function TopCard({ entry, place, onOpenUser }) {
  if (!entry) return <div data-testid={`leaderboard-top-empty-${place}`} className="hidden lg:block" />;
  const isFirst = place === 1;
  return (
    <button
      type="button"
      onClick={() => onOpenUser?.(entry.user_id)}
      data-testid={`leaderboard-top-${place}`}
      className={`relative w-full rounded-2xl border bg-[#0a0a0a] p-5 text-center transition-transform duration-300 hover:-translate-y-1 ${isFirst ? "order-first lg:order-none scale-100 lg:scale-110 border-[#c8ff00]/40 shadow-[0_0_45px_rgba(200,255,0,0.14)]" : "border-[#1a1a1a]"}`}
    >
      <div className={`absolute right-4 top-4 text-[11px] font-bold uppercase tracking-widest ${place === 1 ? "text-[#c8ff00]" : "text-zinc-500"}`}>#{place}</div>
      {isFirst && <Crown className="mx-auto mb-2 text-[#c8ff00]" size={24} />}
      <img src={avatar(entry)} alt="" className="mx-auto h-16 w-16 rounded-full border border-white/10 object-cover" />
      <div className="mt-3 truncate text-sm font-bold text-white">@{entry.username}</div>
      <div className="mt-1 text-xs text-zinc-500">{compact(entry.total_views)} views</div>
      <div className="mt-2 text-lg font-black text-[#c8ff00]">${Number(entry.earned || 0).toFixed(2)}</div>
    </button>
  );
}

export function LeaderboardPage() {
  const [items, setItems] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [period, setPeriod] = useState("all");
  const [category, setCategory] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);

  async function load() {
    setLoading(true); setError(null);
    try {
      const res = await api.get("/leaderboard", { params: { period, category, platform, search: search || undefined } });
      setItems(res.data?.items || []);
      setCurrentUser(res.data?.current_user || null);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [period, category, platform]);

  const top = useMemo(() => [items[1], items[0], items[2]], [items]);
  const lower = items.slice(3);
  const userOutsideTop = currentUser && currentUser.rank > 15;

  return (
    <div data-testid="leaderboard-page" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 data-testid="leaderboard-title" className="page-title text-3xl">Leaderboard</h1>
          <p data-testid="leaderboard-subtext" className="page-subtitle">Top-performing clippers across all campaigns</p>
        </div>
      </div>

      <div data-testid="leaderboard-filter-card" className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a] p-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" /><Input data-testid="leaderboard-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search @username" className="border-[#1a1a1a] bg-black pl-9 text-white" /></div>
          <Select value={period} onValueChange={setPeriod}><SelectTrigger data-testid="leaderboard-time-filter" className="w-[140px] border-[#1a1a1a] bg-black"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Time</SelectItem><SelectItem value="week">This Week</SelectItem><SelectItem value="month">This Month</SelectItem></SelectContent></Select>
          <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="leaderboard-category-filter" className="w-[150px] border-[#1a1a1a] bg-black"><SelectValue /></SelectTrigger><SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c.toLowerCase()}>{c}</SelectItem>)}</SelectContent></Select>
          <Select value={platform} onValueChange={setPlatform}><SelectTrigger data-testid="leaderboard-platform-filter" className="w-[140px] border-[#1a1a1a] bg-black"><SelectValue /></SelectTrigger><SelectContent>{PLATFORMS.map(p => <SelectItem key={p} value={p.toLowerCase()}>{p}</SelectItem>)}</SelectContent></Select>
        </div>
      </div>

      {error && <div data-testid="leaderboard-error" className="rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-300">{error}</div>}

      <div className="grid gap-3 sm:grid-cols-3">
        <TopCard entry={top[0]} place={2} onOpenUser={setProfileUserId} />
        <TopCard entry={top[1]} place={1} onOpenUser={setProfileUserId} />
        <TopCard entry={top[2]} place={3} onOpenUser={setProfileUserId} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1fr_280px]">
        <div className="rounded-2xl border border-[#1a1a1a] bg-[#0a0a0a]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="sr-only"><tr><th>Rank</th><th>User</th><th>Views</th><th>Earnings</th><th>Clips Submitted</th><th>Avg Views / Clip</th></tr></thead>
              <tbody>
                {loading && <tr><td colSpan="6" className="py-8 text-center text-zinc-500">Loading...</td></tr>}
                {!loading && items.slice(3, 50).map((entry) => (
                  <tr
                    key={entry.user_id}
                    data-testid={`leaderboard-row-${entry.rank}`}
                    onClick={() => setProfileUserId(entry.user_id)}
                    className="cursor-pointer border-b border-[#111] transition-colors hover:bg-[#c8ff00]/[0.03] hover:shadow-[inset_2px_0_0_rgba(200,255,0,0.45)]"
                  >
                    <td className="px-4 py-3 font-bold text-zinc-500">#{entry.rank}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><img src={avatar(entry)} alt="" className="h-8 w-8 rounded-full object-cover" /><span className="font-semibold text-white">@{entry.username}</span></div></td>
                    <td className="px-4 py-3 text-right tabular-nums">{compact(entry.total_views)} views</td>
                    <td className="px-4 py-3 text-right font-semibold text-[#c8ff00]">${Number(entry.earned || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">{entry.clip_count} clips</td>
                    <td className="px-4 py-3 text-right">{compact(entry.avg_views)} avg</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!loading && items.length === 0 && <div data-testid="leaderboard-empty" className="px-4 py-12 text-center text-sm text-zinc-600">No leaderboard data yet</div>}
          </div>
        </div>

        {userOutsideTop && (
          <div data-testid="leaderboard-your-rank" className="sticky top-20 h-fit rounded-2xl border border-[#c8ff00]/20 bg-[#c8ff00]/[0.03] p-5">
            <div className="flex items-center gap-2 text-sm font-bold text-white"><Trophy size={16} className="text-[#c8ff00]" /> Your Rank: #{currentUser.rank}</div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs"><div><div className="font-bold text-white">{compact(currentUser.total_views)}</div><div className="text-zinc-600">Views</div></div><div><div className="font-bold text-white">${Number(currentUser.earned).toFixed(2)}</div><div className="text-zinc-600">Earnings</div></div><div><div className="font-bold text-white">{currentUser.clip_count}</div><div className="text-zinc-600">Clips</div></div></div>
          </div>
        )}
      </div>

      <UserProfileDialog
        userId={profileUserId}
        open={!!profileUserId}
        onClose={() => setProfileUserId(null)}
      />
    </div>
  );
}